import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { AgentPresence as Presence, WorkingActivityKind } from "../utils/agent-presence-utils"
import type { HostedAgentSummary } from "@/api/client/hosted-agents"

import { Central, Check, Circle, CircleAlert, Pause, Play, Square } from "@/components/icons"
import { cn } from "@/lib/utils"

import { AgentAvatar } from "./agent-avatar"
import { useAgentMotion } from "../hooks/use-agent-motion"
import { formatAgentDuration } from "../utils/agent-workspace-utils"

type AgentPresenceProps = {
  agent: HostedAgentSummary
  presence: Presence
  activity?: string
  /** Flavour of the working animation, from the tool the latest activity names. */
  activityKind?: WorkingActivityKind | null
  attention?: boolean
  /** Wall-clock duration of the finished turn. Omitted when unknown. */
  durationMs?: number | null
  onStop?: () => void
  onReview?: () => void
  onInspectError?: () => void
}

/** Quiet inline action, so the status stays one 12 px row (#3165). */
const PRESENCE_LINK_CLASS =
  "focus-visible:ring-ring hover:text-foreground cursor-pointer rounded-sm underline underline-offset-2 focus-visible:ring-2 focus-visible:outline-none disabled:cursor-default disabled:no-underline disabled:opacity-60"

/**
 * Persistent, textual status: no hover dependency and no per-token
 * announcements. Since #3165 it is a slim 12 px line above the thread rather
 * than a bar — the character stays (it is the motion system's actor and the
 * only thing that shows WHAT kind of work is running), shrunk to 20 px, and
 * every action that used to be a button is an inline link on the same row.
 */
export function AgentPresence({
  agent,
  presence,
  activity,
  activityKind = null,
  attention = false,
  durationMs = null,
  onStop,
  onReview,
  onInspectError,
}: AgentPresenceProps) {
  const { t, i18n } = useTranslation()
  const motion = useAgentMotion()
  const previous = useRef(presence)
  const [celebrate, setCelebrate] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  useEffect(() => {
    const liveSuccess =
      presence === "done" && ["thinking", "working", "waiting"].includes(previous.current)
    previous.current = presence
    setCelebrate(liveSuccess)
    if (!liveSuccess) return
    const timer = setTimeout(() => setCelebrate(false), 2400)
    return () => clearTimeout(timer)
  }, [presence])
  const needsAttention = presence === "waiting" || presence === "blocked"
  const Icon = {
    idle: Circle,
    thinking: Circle,
    working: Circle,
    waiting: CircleAlert,
    blocked: CircleAlert,
    done: Check,
    stopped: Pause,
    stopping: Pause,
    reconnecting: Central.CloudSimpleDisconnected,
    loading: Circle,
  }[presence]
  // Only a settled turn has a duration worth showing; mid-turn it would tick.
  const duration = presence === "done" ? formatAgentDuration(durationMs, i18n.language || "en") : ""

  return (
    <div
      className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 border-b px-4 py-2.5 text-xs sm:px-6"
      data-presence={presence}
      data-testid="agent-presence"
    >
      <AgentAvatar
        compact
        activityKind={activityKind}
        agent={agent}
        attention={attention}
        celebrate={celebrate}
        className="size-5"
        presence={presence}
      />
      <p
        aria-atomic="true"
        role="status"
        className={cn(
          "flex min-w-0 items-center gap-1.5",
          needsAttention &&
            "border-foreground bg-warning text-warning-foreground rounded-md border px-2 py-0.5 font-medium"
        )}
      >
        <Icon aria-hidden="true" className="size-3.5 shrink-0" />
        <span>{t(`agents.presence.${presence}`)}</span>
        {duration ? <span>· {duration}</span> : null}
        {activity && (presence === "working" || presence === "thinking") ? (
          <span className="min-w-0 truncate" data-testid="agent-live-status">
            · {activity}
          </span>
        ) : null}
      </p>
      <span aria-hidden="true">·</span>
      <button
        aria-expanded={showDetails}
        className={PRESENCE_LINK_CLASS}
        type="button"
        onClick={() => setShowDetails((value) => !value)}
      >
        {t("agents.presence_actions.show_activity")}
      </button>
      {presence === "waiting" && onReview ? (
        <>
          <span aria-hidden="true">·</span>
          <button className={PRESENCE_LINK_CLASS} type="button" onClick={onReview}>
            {t("agents.presence_actions.review")}
          </button>
        </>
      ) : null}
      {presence === "blocked" && onInspectError ? (
        <>
          <span aria-hidden="true">·</span>
          <button className={PRESENCE_LINK_CLASS} type="button" onClick={onInspectError}>
            {t("agents.presence_actions.error")}
          </button>
        </>
      ) : null}
      {onStop && !["stopped", "done", "idle", "loading"].includes(presence) ? (
        <>
          <span aria-hidden="true">·</span>
          <button
            className={cn(PRESENCE_LINK_CLASS, "inline-flex items-center gap-1")}
            disabled={presence === "stopping"}
            type="button"
            onClick={onStop}
          >
            <Square aria-hidden="true" className="size-3" />
            {t(
              presence === "stopping" ? "agents.presence.stopping" : "agents.presence_actions.stop"
            )}
          </button>
        </>
      ) : null}
      <button
        aria-pressed={motion.paused}
        className={cn(PRESENCE_LINK_CLASS, "ml-auto no-underline")}
        type="button"
        aria-label={t(
          motion.paused
            ? "agents.presence_actions.resume_motion"
            : "agents.presence_actions.pause_motion"
        )}
        onClick={motion.toggle}
      >
        {motion.paused ? (
          <Play aria-hidden="true" className="size-3.5" />
        ) : (
          <Pause aria-hidden="true" className="size-3.5" />
        )}
      </button>
      {showDetails ? (
        <p className="basis-full pt-1">{t(`agents.presenceDescription.${presence}`)}</p>
      ) : null}
    </div>
  )
}
